import { useEffect, useMemo, useState } from 'react';
import { createCheckoutSession, getCreditOptions, type CreditOptions } from '../api';

interface CreditAmountPickerProps {
  /** Gates the purchase button — unverified accounts can't check out. */
  isVerified: boolean;
  /** Rendered on the submit button. */
  submitLabel?: string;
  onError?: (message: string | null) => void;
}

function formatCents(cents: number): string {
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(dollars) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(dollars);
}

/**
 * Parse a user-typed dollar amount into whole cents.
 *
 * Returns null for anything that isn't a clean amount so the caller can keep the
 * submit button disabled rather than guessing at what the user meant.
 */
function parseDollarsToCents(input: string): number | null {
  const trimmed = input.trim().replace(/^\$/, '').replace(/,/g, '');
  if (!/^\d*\.?\d{0,2}$/.test(trimmed) || trimmed === '' || trimmed === '.') return null;
  const cents = Math.round(Number.parseFloat(trimmed) * 100);
  return Number.isFinite(cents) ? cents : null;
}

export function CreditAmountPicker({
  isVerified,
  submitLabel = 'Continue to payment',
  onError,
}: CreditAmountPickerProps) {
  const [options, setOptions] = useState<CreditOptions | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);
  const [customInput, setCustomInput] = useState('');
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getCreditOptions()
      .then(result => {
        if (cancelled) return;
        setOptions(result);
        setSelectedPreset(result.presets_cents[0] ?? null);
      })
      .catch(() => {
        // Non-fatal: the picker stays in its loading state and the server
        // re-validates every amount anyway.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const customCents = useMemo(() => parseDollarsToCents(customInput), [customInput]);
  const amountCents = customInput.trim() !== '' ? customCents : selectedPreset;

  const validationMessage = useMemo(() => {
    if (!options) return null;
    if (customInput.trim() !== '' && customCents === null) {
      return 'Enter a dollar amount.';
    }
    if (amountCents === null) return null;
    if (amountCents < options.min_cents) {
      return `Minimum purchase is ${formatCents(options.min_cents)}.`;
    }
    if (amountCents > options.max_cents) {
      return `Maximum single purchase is ${formatCents(options.max_cents)}.`;
    }
    return null;
  }, [options, customInput, customCents, amountCents]);

  const canSubmit =
    isVerified &&
    !isStartingCheckout &&
    options !== null &&
    amountCents !== null &&
    validationMessage === null;

  const handleSelectPreset = (cents: number) => {
    setSelectedPreset(cents);
    setCustomInput('');
    onError?.(null);
  };

  const handleSubmit = async () => {
    if (!canSubmit || amountCents === null) return;
    onError?.(null);
    setIsStartingCheckout(true);
    try {
      const session = await createCheckoutSession({ mode: 'payment', amount_cents: amountCents });
      if (session.url) {
        window.location.href = session.url;
      } else {
        onError?.('Checkout did not return a payment URL.');
        setIsStartingCheckout(false);
      }
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Failed to start checkout');
      setIsStartingCheckout(false);
    }
    // On success the browser navigates away, so the spinner intentionally stays up.
  };

  if (!options) {
    return <div className="text-sm text-dark-300">Loading purchase options...</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {options.presets_cents.map(cents => {
          const isSelected = customInput.trim() === '' && selectedPreset === cents;
          return (
            <button
              key={cents}
              type="button"
              onClick={() => handleSelectPreset(cents)}
              aria-pressed={isSelected}
              className={`flex-1 min-w-[4.5rem] py-2 px-3 rounded-lg border font-medium transition-colors duration-200 ${
                isSelected
                  ? 'bg-primary-600 border-primary-500 text-white'
                  : 'bg-dark-700 border-dark-500 text-dark-100 hover:bg-dark-600'
              }`}
            >
              {formatCents(cents)}
            </button>
          );
        })}
      </div>

      <label className="block">
        <span className="text-xs text-dark-400">Or enter an amount</span>
        <div className="mt-1 flex items-center gap-2 rounded-lg border border-dark-500 bg-dark-700 px-3 focus-within:ring-2 focus-within:ring-primary-500">
          <span className="text-dark-300">$</span>
          <input
            type="text"
            inputMode="decimal"
            value={customInput}
            onChange={e => {
              setCustomInput(e.target.value);
              onError?.(null);
            }}
            placeholder={(options.min_cents / 100).toFixed(2)}
            aria-label="Custom credit amount in dollars"
            aria-invalid={validationMessage !== null}
            className="flex-1 bg-transparent py-2 text-dark-50 placeholder-dark-400 focus:outline-none"
          />
        </div>
      </label>

      {validationMessage ? (
        <div className="text-xs text-amber-300">{validationMessage}</div>
      ) : (
        <div className="text-xs text-dark-400">
          Minimum {formatCents(options.min_cents)}. Credits are consumed as AI calls are made.
        </div>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        title={!isVerified ? 'Verify your email to purchase credits' : undefined}
        className="btn-primary w-full"
      >
        {isStartingCheckout
          ? 'Opening checkout...'
          : amountCents !== null && validationMessage === null
            ? `${submitLabel} — ${formatCents(amountCents)}`
            : submitLabel}
      </button>
    </div>
  );
}
